import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingService, trackActivity, type Plan, type SubscriptionStatus } from '../services/api';
import { Check, X } from 'lucide-react';

const currencySymbol: Record<string, string> = {
  EUR: '\u20AC',
  USD: '$',
  GBP: '\u00A3',
};

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [subRes, plansRes, paymentsRes] = await Promise.all([
          billingService.getSubscription(),
          billingService.getPlans(),
          billingService.isPaymentsEnabled(),
        ]);
        setSub(subRes.data);
        setPlans(plansRes.data.plans);
        setPaymentsEnabled(paymentsRes.data.enabled);
      } catch {
        setSub(null);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const monthlyPlan = plans.find(p => p.id === 'pro-monthly');
  const yearlyPlan = plans.find(p => p.id === 'pro-yearly');

  const isMonthly = billingInterval === 'month';
  const activePlan = isMonthly ? monthlyPlan : yearlyPlan;
  const planAmount = activePlan?.amount ?? (isMonthly ? 9.99 : 19.99);
  const planCurrency = activePlan?.currency ?? 'EUR';
  const symbol = currencySymbol[planCurrency] || planCurrency;
  const planId = activePlan?.id || (isMonthly ? 'pro-monthly' : 'pro-yearly');

  const yearlyVsMonthly = monthlyPlan?.amount && yearlyPlan?.amount
    ? Math.round((1 - yearlyPlan.amount / (monthlyPlan.amount * 12)) * 100)
    : 0;

  const handleSubscribe = async (planId: string) => {
    setActionLoading(planId);
    try {
      const res = await billingService.createCheckout(planId);
      window.location.href = res.data.url;
      trackActivity({ event: 'subscribe_started', feature: 'pricing', success: true, metadata: { plan: planId } });
    } catch (err) {
      console.error('Checkout failed', err);
      trackActivity({
        event: 'subscribe_started',
        feature: 'pricing',
        success: false,
        error: err instanceof Error ? err.message : 'Checkout failed',
        metadata: { plan: planId },
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleManage = async () => {
    setActionLoading('portal');
    try {
      const res = await billingService.createPortal();
      window.location.href = res.data.url;
      trackActivity({ event: 'open_billing_portal', feature: 'pricing', success: true });
    } catch (err) {
      console.error('Portal failed', err);
      trackActivity({
        event: 'open_billing_portal',
        feature: 'pricing',
        success: false,
        error: err instanceof Error ? err.message : 'Portal failed',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const isPro = sub?.isPro;

  const freeFeatures = [
    { label: 'Preview all templates', enabled: true },
    { label: 'AI content enrichment', enabled: true },
    { label: 'Job matching', enabled: true },
    { label: 'PDF downloads', enabled: false },
  ];

  const proFeatures = [
    { label: 'Preview all templates', enabled: true },
    { label: 'AI content enrichment', enabled: true },
    { label: 'Job matching', enabled: true },
    { label: 'Unlimited PDF downloads', enabled: true },
  ];

  return (
    <div className="max-w-[900px] mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-3xl font-semibold text-foreground mb-2 font-display tracking-tight">Choose Your Plan</h1>
        <p className="text-base text-secondary-foreground">Unlock unlimited resume downloads with Pro</p>
      </div>

      {!paymentsEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-card p-6 text-center mb-8">
          <p className="text-amber-800 font-medium mb-1">Payments are temporarily unavailable</p>
          <p className="text-sm text-amber-700">
            You can download once payments are re-enabled and you have an active subscription.
            {sub?.isPro && ' Your Pro subscription remains active.'}
          </p>
          <button
            className="mt-4 h-9 px-4 bg-white border border-amber-300 text-amber-800 text-sm font-medium rounded-btn hover:bg-amber-100 transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      )}

      <div className="flex justify-center gap-0 mb-8 bg-subtle rounded-card p-1 max-w-[300px] mx-auto">
        <button
          className={`flex-1 px-6 py-2.5 border-none bg-transparent text-secondary-foreground text-sm font-medium cursor-pointer rounded-[calc(12px-4px)] transition-all duration-200 ${isMonthly ? 'bg-white text-foreground shadow-soft' : ''}`}
          onClick={() => setBillingInterval('month')}
        >
          Monthly
        </button>
        <button
          className={`flex-1 px-6 py-2.5 border-none bg-transparent text-secondary-foreground text-sm font-medium cursor-pointer rounded-[calc(12px-4px)] transition-all duration-200 ${!isMonthly ? 'bg-white text-foreground shadow-soft' : ''}`}
          onClick={() => setBillingInterval('year')}
        >
          Yearly
          {yearlyVsMonthly > 0 && (
            <span className="inline-block bg-emerald-500 text-white text-[0.65rem] font-semibold px-1.5 py-0.5 rounded-full ml-1 align-middle">Save {yearlyVsMonthly}%</span>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        <div className="bg-white border border-zinc-200 rounded-card overflow-hidden transition-transform duration-200 relative hover:-translate-y-1 hover:shadow-elevated">
          <div className="text-center px-6 py-8 pb-6 border-b border-zinc-100">
            <h2 className="text-xl text-foreground mb-2 font-display font-semibold">Free</h2>
            <div className="text-4xl font-bold text-foreground">{symbol}0</div>
            <p className="text-sm text-secondary-foreground mt-1">forever</p>
          </div>
          <div className="p-6 space-y-2">
            {freeFeatures.map((feature) => (
              <div key={feature.label} className={`flex items-center gap-2 py-2 text-sm ${feature.enabled ? 'text-foreground' : 'text-secondary-foreground opacity-50'}`}>
                {feature.enabled ? (
                  <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <X className="w-3.5 h-3.5 text-secondary-foreground shrink-0" />
                )}
                <span>{feature.label}</span>
              </div>
            ))}
          </div>
          <div className="px-6 pb-6 text-center">
            {isPro ? (
              <span className="inline-block text-sm text-secondary-foreground font-medium">Free Plan</span>
            ) : (
              <span className="inline-block text-sm text-secondary-foreground font-medium">Current Plan</span>
            )}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-card overflow-hidden transition-transform duration-200 relative hover:-translate-y-1 hover:shadow-elevated border-brand-500 shadow-[0_0_0_1px_#4F46E5]">
          <div className="absolute top-3 right-3 bg-brand-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
            {isMonthly ? 'Most Popular' : 'Best Value'}
          </div>
          <div className="text-center px-6 py-8 pb-6 border-b border-zinc-100">
            <h2 className="text-xl text-foreground mb-2 font-display font-semibold">Pro {isMonthly ? 'Monthly' : 'Yearly'}</h2>
            <div className="text-4xl font-bold text-foreground">{symbol}{planAmount}</div>
            <p className="text-sm text-secondary-foreground mt-1">per {isMonthly ? 'month' : 'year'}</p>
          </div>
          <div className="p-6 space-y-2">
            {proFeatures.map((feature) => (
              <div key={feature.label} className="flex items-center gap-2 py-2 text-sm text-foreground">
                <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span>{feature.label}</span>
              </div>
            ))}
          </div>
          <div className="px-6 pb-6 text-center">
            {isPro ? (
              <button
                className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors w-full"
                onClick={handleManage}
                disabled={actionLoading === 'portal'}
              >
                {actionLoading === 'portal' ? 'Loading...' : 'Manage Subscription'}
              </button>
            ) : paymentsEnabled ? (
              <button
                className="h-10 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-btn transition-all duration-200 w-full"
                onClick={() => handleSubscribe(planId)}
                disabled={actionLoading === planId}
              >
                {actionLoading === planId ? 'Redirecting...' : 'Subscribe Now'}
              </button>
            ) : (
              <span className="inline-block text-sm text-secondary-foreground font-medium py-2.5">Payments unavailable</span>
            )}
          </div>
        </div>
      </div>

      <div className="text-center mt-8 text-secondary-foreground">
        <p className="mb-4 text-sm">All plans include a 14-day free trial. Cancel anytime.</p>
        <button
          className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors"
          onClick={() => navigate('/dashboard')}
        >
          Continue with Free Plan
        </button>
      </div>
    </div>
  );
};

export default Pricing;
